import React from 'react';
import type { Connector, NetworkStatus } from '../../../../app/ApiGen';
import { getStatusIndicator } from '../../../../components/Connectors';
import {
  filterBySelectableItem,
  SelectableFilters,
  truncateAppliedFilters,
} from '../../../jobs/filterComponents/SelectableFilters';
import type { FilterComponentProps } from '../../../jobs/filterComponents/types';

// Labels come from the same StatusIndicators table the Connectors component renders, so the
// filter options stay in sync with the dots the user sees in the column.
const CONNECTOR_STATUS_LABELS = [
  'Available',
  'Charging',
  'Faulted',
  'Finishing',
  'Never connected',
  'Offline',
  'Preparing',
  'Reserved',
  'Suspended EV',
  'Suspended EVSE',
  'Unavailable',
] as const;

export type ConnectorStatusLabel = (typeof CONNECTOR_STATUS_LABELS)[number];

type ConnectorStatusFilterValues = Record<ConnectorStatusLabel, boolean>;

const defaultFilters: ConnectorStatusFilterValues = CONNECTOR_STATUS_LABELS.reduce(
  (acc, label) => ({ ...acc, [label]: false }),
  {} as ConnectorStatusFilterValues,
);

const options = CONNECTOR_STATUS_LABELS.map((label) => [label, label] as [string, string]);

function renderValues(value: [string, boolean][]): string {
  return truncateAppliedFilters(value.filter(([, applied]) => applied).map(([label]) => label));
}

export const ConnectorStatusFilters: React.FC<FilterComponentProps> = ({ columnDef, onFilterChanged }) => (
  <SelectableFilters<ConnectorStatusLabel, ConnectorStatusFilterValues>
    columnDef={columnDef}
    defaultFilters={defaultFilters}
    onFilterChanged={onFilterChanged}
    options={options}
    renderValues={renderValues}
  />
);

// A row matches if any of its connectors' effective status label is currently selected in the
// filter. No selection means everything passes (handled by filterBySelectableItem).
export function filterByConnectorStatus(
  search: unknown,
  item: { connectors: Pick<Connector, 'connectorId' | 'ocppStatus'>[]; networkStatus: NetworkStatus },
): boolean {
  return item.connectors.some((connector) => {
    const { label } = getStatusIndicator(item.networkStatus, connector);
    return filterBySelectableItem<ConnectorStatusLabel>(search, { value: label as ConnectorStatusLabel });
  });
}
